/**
 * Mobile Carousel Component with Swipe Gestures
 * Provides touch-friendly carousel functionality for mobile devices
 */

interface CarouselOptions {
  container: HTMLElement;
  itemSelector: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  swipeThreshold?: number;
  snapToItems?: boolean;
}

interface TouchState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  isDragging: boolean;
  startTime: number;
}

export class MobileCarousel {
  private container: HTMLElement;
  private itemsContainer!: HTMLElement;
  private items!: HTMLElement[];
  private options: Required<Omit<CarouselOptions, 'container'>>;
  private currentIndex: number = 0;
  private touchState: TouchState = {
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    isDragging: false,
    startTime: 0
  };
  private autoPlayTimer?: number;
  private dotsContainer?: HTMLElement;
  private prevButton?: HTMLButtonElement;
  private nextButton?: HTMLButtonElement;
  private resizeObserver?: ResizeObserver;

  constructor(options: CarouselOptions) {
    this.container = options.container;
    this.options = {
      itemSelector: options.itemSelector,
      autoPlay: options.autoPlay ?? false,
      autoPlayInterval: options.autoPlayInterval ?? 5000,
      showDots: options.showDots ?? true,
      showArrows: options.showArrows ?? false,
      swipeThreshold: options.swipeThreshold ?? 50,
      snapToItems: options.snapToItems ?? true
    };

    this.init();
  }

  private init(): void {
    this.setupContainer();
    this.setupItems();
    this.setupControls();
    this.setupTouchEvents();
    this.setupKeyboardEvents();
    this.setupResizeObserver();
    
    if (this.options.autoPlay) {
      this.startAutoPlay();
    }

    // Initial positioning
    this.updatePosition(false);
  }

  private setupContainer(): void {
    // Create items container if it doesn't exist
    this.itemsContainer = this.container.querySelector('.carousel-items') as HTMLElement;
    
    if (!this.itemsContainer) {
      this.itemsContainer = document.createElement('div');
      this.itemsContainer.className = 'carousel-items';
      
      // Move existing items into the container
      const existingItems = Array.from(this.container.children);
      existingItems.forEach(item => {
        if (!item.classList.contains('carousel-controls')) {
          this.itemsContainer.appendChild(item);
        }
      });
      
      this.container.appendChild(this.itemsContainer);
    }

    // Add carousel classes
    this.container.classList.add('mobile-carousel');
    this.itemsContainer.classList.add('carousel-items');

    // Set up CSS custom properties for smooth scrolling
    this.container.style.setProperty('--carousel-transition', 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)');
  }

  private setupItems(): void {
    this.items = Array.from(this.itemsContainer.querySelectorAll(this.options.itemSelector));
    
    this.items.forEach((item, index) => {
      item.classList.add('carousel-item');
      item.setAttribute('data-carousel-index', index.toString());
      
      // Add touch-action for better touch handling
      item.style.touchAction = 'pan-y pinch-zoom';
    });

    // Set initial item width
    this.updateItemWidths();
  }

  private setupControls(): void {
    let controlsContainer = this.container.querySelector('.carousel-controls') as HTMLElement;
    
    if (!controlsContainer) {
      controlsContainer = document.createElement('div');
      controlsContainer.className = 'carousel-controls';
      this.container.appendChild(controlsContainer);
    }

    // Setup dots navigation
    if (this.options.showDots && this.items.length > 1) {
      this.setupDots(controlsContainer);
    }

    // Setup arrow navigation
    if (this.options.showArrows && this.items.length > 1) {
      this.setupArrows(controlsContainer);
    }
  }

  private setupDots(container: HTMLElement): void {
    this.dotsContainer = document.createElement('div');
    this.dotsContainer.className = 'carousel-dots';
    this.dotsContainer.setAttribute('role', 'tablist');
    this.dotsContainer.setAttribute('aria-label', 'Carousel navigation');

    this.items.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = 'carousel-dot';
      dot.setAttribute('type', 'button');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      dot.addEventListener('click', () => this.goToSlide(index));
      
      this.dotsContainer!.appendChild(dot);
    });

    container.appendChild(this.dotsContainer);
  }

  private setupArrows(container: HTMLElement): void {
    const arrowsContainer = document.createElement('div');
    arrowsContainer.className = 'carousel-arrows';

    // Previous button
    this.prevButton = document.createElement('button');
    this.prevButton.className = 'carousel-arrow carousel-arrow--prev';
    this.prevButton.setAttribute('type', 'button');
    this.prevButton.setAttribute('aria-label', 'Previous slide');
    this.prevButton.innerHTML = '<span aria-hidden="true">‹</span>';
    this.prevButton.addEventListener('click', () => this.previousSlide());

    // Next button
    this.nextButton = document.createElement('button');
    this.nextButton.className = 'carousel-arrow carousel-arrow--next';
    this.nextButton.setAttribute('type', 'button');
    this.nextButton.setAttribute('aria-label', 'Next slide');
    this.nextButton.innerHTML = '<span aria-hidden="true">›</span>';
    this.nextButton.addEventListener('click', () => this.nextSlide());

    arrowsContainer.appendChild(this.prevButton);
    arrowsContainer.appendChild(this.nextButton);
    container.appendChild(arrowsContainer);
  }

  private setupTouchEvents(): void {
    // Touch events
    this.itemsContainer.addEventListener('touchstart', this.handleTouchStart.bind(this) as EventListener, { passive: false });
    this.itemsContainer.addEventListener('touchmove', this.handleTouchMove.bind(this) as EventListener, { passive: false });
    this.itemsContainer.addEventListener('touchend', this.handleTouchEnd.bind(this) as EventListener, { passive: true });

    // Mouse events for desktop testing
    this.itemsContainer.addEventListener('mousedown', this.handleMouseDown.bind(this) as EventListener);
    this.itemsContainer.addEventListener('mousemove', this.handleMouseMove.bind(this) as EventListener);
    this.itemsContainer.addEventListener('mouseup', this.handleMouseUp.bind(this) as EventListener);
    this.itemsContainer.addEventListener('mouseleave', this.handleMouseUp.bind(this) as EventListener);

    // Prevent context menu on long press
    this.itemsContainer.addEventListener('contextmenu', (e: Event) => {
      if (this.touchState.isDragging) {
        e.preventDefault();
      }
    });
  }

  private setupKeyboardEvents(): void {
    this.container.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.previousSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.items.length - 1);
          break;
      }
    });
  }

  private setupResizeObserver(): void {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateItemWidths();
        this.updatePosition(false);
      });
      
      this.resizeObserver.observe(this.container);
    } else {
      // Fallback for browsers without ResizeObserver
      const resizeHandler = () => {
        this.updateItemWidths();
        this.updatePosition(false);
      };
      (window as any).addEventListener('resize', resizeHandler);
    }
  }

  private handleTouchStart(e: TouchEvent): void {
    const touch = e.touches[0];
    if (!touch) return;
    
    this.touchState = {
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: touch.clientX,
      currentY: touch.clientY,
      isDragging: true,
      startTime: Date.now()
    };

    this.stopAutoPlay();
    this.itemsContainer.style.transition = 'none';
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.touchState.isDragging) return;

    const touch = e.touches[0];
    if (!touch) return;
    
    this.touchState.currentX = touch.clientX;
    this.touchState.currentY = touch.clientY;

    const deltaX = this.touchState.currentX - this.touchState.startX;
    const deltaY = this.touchState.currentY - this.touchState.startY;

    // Determine if this is a horizontal swipe
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault(); // Prevent vertical scrolling
      
      const containerWidth = this.container.offsetWidth;
      const offset = (-this.currentIndex * containerWidth) + deltaX;
      
      this.itemsContainer.style.transform = `translateX(${offset}px)`;
    }
  }

  private handleTouchEnd(_e: TouchEvent): void {
    if (!this.touchState.isDragging) return;

    const deltaX = this.touchState.currentX - this.touchState.startX;
    const deltaTime = Date.now() - this.touchState.startTime;
    const velocity = Math.abs(deltaX) / deltaTime;

    // Determine if swipe was significant enough
    const isSignificantSwipe = Math.abs(deltaX) > this.options.swipeThreshold || velocity > 0.5;

    if (isSignificantSwipe) {
      if (deltaX > 0 && this.currentIndex > 0) {
        this.previousSlide();
      } else if (deltaX < 0 && this.currentIndex < this.items.length - 1) {
        this.nextSlide();
      } else {
        this.updatePosition(true);
      }
    } else {
      this.updatePosition(true);
    }

    this.touchState.isDragging = false;
    
    if (this.options.autoPlay) {
      this.startAutoPlay();
    }
  }

  private handleMouseDown(e: MouseEvent): void {
    e.preventDefault();
    this.touchState = {
      startX: e.clientX,
      startY: e.clientY,
      currentX: e.clientX,
      currentY: e.clientY,
      isDragging: true,
      startTime: Date.now()
    };

    this.stopAutoPlay();
    this.itemsContainer.style.transition = 'none';
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.touchState.isDragging) return;

    this.touchState.currentX = e.clientX;
    this.touchState.currentY = e.clientY;

    const deltaX = this.touchState.currentX - this.touchState.startX;
    const containerWidth = this.container.offsetWidth;
    const offset = (-this.currentIndex * containerWidth) + deltaX;
    
    this.itemsContainer.style.transform = `translateX(${offset}px)`;
  }

  private handleMouseUp(_e: MouseEvent): void {
    if (!this.touchState.isDragging) return;

    const deltaX = this.touchState.currentX - this.touchState.startX;
    const deltaTime = Date.now() - this.touchState.startTime;
    const velocity = Math.abs(deltaX) / deltaTime;

    const isSignificantSwipe = Math.abs(deltaX) > this.options.swipeThreshold || velocity > 0.5;

    if (isSignificantSwipe) {
      if (deltaX > 0 && this.currentIndex > 0) {
        this.previousSlide();
      } else if (deltaX < 0 && this.currentIndex < this.items.length - 1) {
        this.nextSlide();
      } else {
        this.updatePosition(true);
      }
    } else {
      this.updatePosition(true);
    }

    this.touchState.isDragging = false;
    
    if (this.options.autoPlay) {
      this.startAutoPlay();
    }
  }

  private updateItemWidths(): void {
    const containerWidth = this.container.offsetWidth;
    
    this.items.forEach(item => {
      (item as HTMLElement).style.width = `${containerWidth}px`;
    });

    this.itemsContainer.style.width = `${containerWidth * this.items.length}px`;
  }

  private updatePosition(animate: boolean = true): void {
    const containerWidth = this.container.offsetWidth;
    const offset = -this.currentIndex * containerWidth;

    if (animate) {
      this.itemsContainer.style.transition = 'var(--carousel-transition)';
    } else {
      this.itemsContainer.style.transition = 'none';
    }

    this.itemsContainer.style.transform = `translateX(${offset}px)`;

    // Update dots
    if (this.dotsContainer) {
      const dots = this.dotsContainer.querySelectorAll('.carousel-dot');
      dots.forEach((dot, index) => {
        dot.classList.toggle('carousel-dot--active', index === this.currentIndex);
        dot.setAttribute('aria-selected', index === this.currentIndex ? 'true' : 'false');
      });
    }

    // Update arrows
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex === this.items.length - 1;
    }

    // Update ARIA live region
    this.announceSlideChange();
  }

  private announceSlideChange(): void {
    // Create or update live region for screen readers
    let liveRegion = this.container.querySelector('.carousel-live-region') as HTMLElement;
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'carousel-live-region sr-only';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      this.container.appendChild(liveRegion);
    }

    liveRegion.textContent = `Slide ${this.currentIndex + 1} of ${this.items.length}`;
  }

  public goToSlide(index: number): void {
    if (index < 0 || index >= this.items.length || index === this.currentIndex) {
      return;
    }

    this.currentIndex = index;
    this.updatePosition(true);
  }

  public nextSlide(): void {
    if (this.currentIndex < this.items.length - 1) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  public previousSlide(): void {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  public startAutoPlay(): void {
    if (!this.options.autoPlay) return;

    this.stopAutoPlay();
    this.autoPlayTimer = window.setInterval(() => {
      if (this.currentIndex < this.items.length - 1) {
        this.nextSlide();
      } else {
        this.goToSlide(0); // Loop back to first slide
      }
    }, this.options.autoPlayInterval);
  }

  public stopAutoPlay(): void {
    if (this.autoPlayTimer !== undefined) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = undefined as any;
    }
  }

  public destroy(): void {
    this.stopAutoPlay();
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Remove event listeners
    this.itemsContainer.removeEventListener('touchstart', this.handleTouchStart.bind(this));
    this.itemsContainer.removeEventListener('touchmove', this.handleTouchMove.bind(this));
    this.itemsContainer.removeEventListener('touchend', this.handleTouchEnd.bind(this));

    // Clean up DOM
    this.container.classList.remove('mobile-carousel');
    
    const controlsContainer = this.container.querySelector('.carousel-controls');
    if (controlsContainer) {
      controlsContainer.remove();
    }

    const liveRegion = this.container.querySelector('.carousel-live-region');
    if (liveRegion) {
      liveRegion.remove();
    }
  }

  // Getters
  public get currentSlide(): number {
    return this.currentIndex;
  }

  public get totalSlides(): number {
    return this.items.length;
  }

  public get isAutoPlaying(): boolean {
    return this.autoPlayTimer !== undefined;
  }
}